import { Observable, delay, map, of, switchMap, throwError } from 'rxjs'
import { Injectable, inject } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { environment } from '@monorepo-channels/shared/util-environments'
import { Client } from '../models/client/client'
import { Agreements, ClientProps } from '../models/client/client.interface'

@Injectable({ providedIn: 'root' })
export class ClientGateway {
	private baseURL = `${environment.channelsAPIURL}/api`
	private http = inject(HttpClient)

	updateClientBackend(clientId: string, updateClientDto: UpdateClientDto) {
		return this.http.patch(`${this.baseURL}/v1/clients/${clientId}`, updateClientDto)
	}

	loginHomologation(clientId: string) {
		return this.http
			.get<ClientProps>(`${this.baseURL}/v1/auth/login/homologation/${clientId}`)
			.pipe(map(client => Client.create(client)))
	}

	loginClient(clientId: string): Observable<Client> {
		return this.http
			.get<ClientProps>(`${this.baseURL}/v1/clients/${clientId}`, { headers: { clientId } })
			.pipe(map(client => Client.create(client)))
	}
	loginClientByEmailAndPhone(email: string, phone: string): Observable<Client> {
		return this.http
			.post<ClientProps>(`${this.baseURL}/v1/auth/bradesco/`, { email, phone })
			.pipe(map(client => Client.create(client)))
	}

	loginLiveloSSO(code: string, codeVerifier: string | null): Observable<Client> {
		return this.http
			.post<ClientProps>(`${this.baseURL}/v1/auth/bb-livelo`, { code, codeVerifier })
			.pipe(map(client => Client.create(client)))
	}

	loginUnicredSSO(unicredToken: string): Observable<Client> {
		return this.http
			.post<ClientProps>(`${this.baseURL}/v1/auth/unicred`, { unicredToken })
			.pipe(map(client => Client.create(client)))
	}

	loginCpfProgramaMenu(dto: LoginCpfPasswordDto) {
		return this.http
			.post<ClientProps>(`${this.baseURL}/v1/auth/programa-menu/loginPassword`, dto)
			.pipe(map(client => Client.create(client)))
	}

	isLoggedIn() {
		return this.http.get(`${this.baseURL}/v1/auth/isLoggedIn`)
	}

	encrypt(body: EncryptCardDataDto) {
		// Uncomment the scenario you want to test
		// return this.errorScenario() as unknown as Observable<{ cardData: string }>
		// return this.successScenario({ cardData: '1234321' })
		return this.http.post<{ cardData: string }>(`${this.baseURL}/v1/payments/encrypt`, body)
	}

	createCardToken(createCard: CreateCardDto) {
		// Uncomment the scenario you want to test
		// return this.errorScenario()
		// return this.successScenario({ gatewayId: '123', lastFourDigits: '4321' })
		return this.http.post<{ gatewayId: string; lastFourDigits: string }>(
			`${this.baseURL}/v2/payments/create-card-token`,
			createCard
		)
	}

	removeCard(clientId: string, lastFourDigits: string) {
		// Uncomment the scenario you want to test
		// return this.errorScenario()
		return this.http.delete(`${this.baseURL}/v1/payments/remove-card/${clientId}/${lastFourDigits}`)
	}

	removeCategory(clientId: string, categoryId: string) {
		// Uncomment the scenario you want to test
		// return this.errorScenario()
		return this.http.delete(`${this.baseURL}/v1/payments/remove-category/${clientId}/${categoryId}`)
	}

	updateMainCard(clientId: string, lastFourDigits: string) {
		// Uncomment the scenario you want to test
		// return this.errorScenario()
		return this.http.patch(`${this.baseURL}/v1/payments/main-card/${clientId}/${lastFourDigits}`, {})
	}

	private successScenario<T>(data: T) {
		return of(data).pipe(delay(1000))
	}

	private errorScenario() {
		return of(['1']).pipe(
			delay(1000),
			switchMap(() => throwError(() => ({ error: { message: 'error' } })))
		)
	}
}

export interface EncryptCardDataDto {
	number: string
	cvv: string
	holder: string
	expMonth: number
	expYear: number
}

export interface BillingAddress {
	address: string
	zipCode: string
	city: string
	state: string
	country: string
}

export interface UpdateClientDto {
	name?: string
	username?: string
	phone?: string
	agreements?: Agreements
	favorites?: string[]
}

export interface CardPropertiesDto {
	number: string
	cvv: string
	holder: string
	expMonth: number
	expYear: number
	billingAddress: BillingAddress
}

export interface CreateCardDto {
	cardProperties: CardPropertiesDto
	clientId: string
	cpf?: string // from input client, compare with client.cpf from db
	categoryId?: string
	mainCard?: boolean
	verifyCard?: boolean
}

export interface LoginCpfPasswordDto {
	phone: string
	email: string
	cpf: string
	password: string
}
